import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

/**
 * Modern, theme-aware Loader component for quiz loading states
 * Shows custom messages and smooth animations
 */
const Loader = ({ title, message }) => {
  const { isDark } = useTheme();

  return (
    <div className={`flex items-center justify-center min-h-screen ${isDark ? 'bg-dark-primary' : 'bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50'
      }`}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className={`relative rounded-2xl shadow-2xl p-8 md:p-12 text-center max-w-md mx-4 ${isDark
            ? 'bg-dark-secondary/80 backdrop-blur-md border border-white/10'
            : 'bg-white/90 backdrop-blur-md border border-purple-100'
          }`}
      >
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 rounded-2xl overflow-hidden opacity-30">
          <motion.div
            animate={{
              background: [
                'linear-gradient(45deg, #9333ea 0%, #db2777 100%)',
                'linear-gradient(45deg, #db2777 0%, #9333ea 100%)',
                'linear-gradient(45deg, #9333ea 0%, #db2777 100%)',
              ]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute inset-0 blur-2xl"
          />
        </div>

        <div className="relative z-10">
          {/* Spinner */}
          <div className="flex justify-center mb-6">
            <div className="relative w-16 h-16">
              {/* Outer ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-0 rounded-full border-4 border-transparent border-t-purple-600 border-r-pink-600"
              />
              {/* Inner ring */}
              <motion.div
                animate={{ rotate: -360 }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-2 rounded-full border-4 border-transparent border-b-purple-400 border-l-pink-400"
              />
              {/* Center dot */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-5 rounded-full bg-gradient-to-br from-purple-600 to-pink-600"
              />
            </div>
          </div>

          {/* Title */}
          <motion.h1
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'
              }`}
          >
            {title || 'Just a moment...'}
          </motion.h1>

          {/* Message */}
          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
          >
            {message || 'We are loading your content, please wait.'}
          </motion.p>

          {/* Animated dots */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex justify-center space-x-2 mt-6"
          >
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: index * 0.15,
                  ease: "easeInOut"
                }}
                className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600"
              />
            ))}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

Loader.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
};

export default Loader;
